import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mic, Square, Upload, Trash2, Music, Loader2 } from 'lucide-react';
import { apiGet, apiPost, apiDel } from '../../lib/api.js';
import {
  blobToWav, blobToDataUrl, VOICEMAIL_MAX_BYTES, VOICEMAIL_MAX_SECONDS,
} from '../../lib/audioToWav.js';
import { Card, LoadingPanel, EmptyPanel } from './_shared.jsx';

/**
 * Settings → VoiceMail Drop: record from the microphone or upload an .mp3/.wav,
 * name it, preview it, and manage the drops used from the softphone.
 * Everything is normalised to a small WAV so Twilio can play it.
 * @returns {JSX.Element}
 */
export default function VoicemailDropTab() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [pending, setPending] = useState(null); // { blob, url, durationSec }
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const recRef = useRef({ rec: null, chunks: [], timer: null, stream: null });

  const { data, isLoading } = useQuery({
    queryKey: ['phone-voicemails'],
    queryFn: () => apiGet('/phone/voicemails'),
  });
  const drops = data?.data?.voicemails || [];

  useEffect(() => () => {
    clearInterval(recRef.current.timer);
    recRef.current.stream?.getTracks().forEach((t) => t.stop());
  }, []);

  /** Normalise raw audio to WAV and stage it for naming/saving. */
  async function stage(blob) {
    try {
      const wav = await blobToWav(blob);
      if (wav.blob.size > VOICEMAIL_MAX_BYTES) throw new Error('Recording is too large. Keep it under a minute.');
      if (pending?.url) URL.revokeObjectURL(pending.url);
      setPending({ ...wav, url: URL.createObjectURL(wav.blob) });
    } catch (e) {
      toast.error(e.message || 'Could not read that audio');
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const state = recRef.current;
      state.rec = rec; state.chunks = []; state.stream = stream;
      rec.ondataavailable = (e) => { if (e.data.size) state.chunks.push(e.data); };
      rec.onstop = async () => {
        clearInterval(state.timer);
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        await stage(new Blob(state.chunks, { type: rec.mimeType || 'audio/webm' }));
      };
      rec.start();
      setElapsed(0);
      setRecording(true);
      state.timer = setInterval(() => setElapsed((s) => {
        if (s + 1 >= VOICEMAIL_MAX_SECONDS) rec.state === 'recording' && rec.stop();
        return s + 1;
      }), 1000);
    } catch {
      toast.error('Microphone access was blocked. Allow it in your browser to record.');
    }
  }

  function stopRecording() {
    const { rec } = recRef.current;
    if (rec && rec.state === 'recording') rec.stop();
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(mp3|wav)$/i.test(file.name) && !/audio\/(mpeg|mp3|wav|x-wav|wave)/.test(file.type)) {
      toast.error('Upload an .mp3 or .wav file');
      return;
    }
    if (!name.trim()) setName(file.name.replace(/\.[^.]+$/, ''));
    stage(file);
  }

  async function save() {
    if (!name.trim()) { toast.error('Give this voicemail a name'); return; }
    if (!pending) { toast.error('Record or upload audio first'); return; }
    setSaving(true);
    try {
      await apiPost('/phone/voicemails', {
        name: name.trim(),
        audioBase64: await blobToDataUrl(pending.blob),
        mimeType: 'audio/wav',
        durationSec: pending.durationSec,
      });
      toast.success('Voicemail drop saved');
      URL.revokeObjectURL(pending.url);
      setPending(null);
      setName('');
      qc.invalidateQueries({ queryKey: ['phone-voicemails'] });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not save voicemail');
    } finally {
      setSaving(false);
    }
  }

  async function remove(vm) {
    if (!window.confirm(`Delete "${vm.name}"?`)) return;
    try {
      await apiDel(`/phone/voicemails/${vm.id}`);
      qc.invalidateQueries({ queryKey: ['phone-voicemails'] });
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  }

  return (
    <div className="space-y-4">
      <Card
        title="VoiceMail Drop"
        description="Pre-recorded messages you can drop on a lead's voicemail from the softphone, then move straight to the next call."
      >
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="label" htmlFor="vm-name">Name</label>
            <input
              id="vm-name"
              className="input mt-1 w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First Touch"
              maxLength={60}
            />
          </div>
          <div className="flex gap-2">
            {recording ? (
              <button type="button" className="btn-primary bg-red-600 hover:bg-red-500 flex items-center gap-2" onClick={stopRecording}>
                <Square className="w-4 h-4" /> Stop ({elapsed}s)
              </button>
            ) : (
              <button type="button" className="btn-primary flex items-center gap-2" onClick={startRecording}>
                <Mic className="w-4 h-4" /> Record
              </button>
            )}
            <button type="button" className="btn-secondary flex items-center gap-2" onClick={() => fileRef.current?.click()} disabled={recording}>
              <Upload className="w-4 h-4" /> Upload .mp3 / .wav
            </button>
            <input ref={fileRef} type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" className="hidden" onChange={onFile} />
          </div>
        </div>

        {pending && (
          <div className="mt-4 p-3 rounded-lg border border-civix-border bg-civix-panelLight space-y-2">
            <div className="text-xs text-white/50">Preview ({pending.durationSec}s). Re-record or upload to replace.</div>
            <audio controls src={pending.url} className="w-full" />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => { URL.revokeObjectURL(pending.url); setPending(null); }}>Discard</button>
              <button type="button" className="btn-primary flex items-center gap-2" onClick={save} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save voicemail
              </button>
            </div>
          </div>
        )}
      </Card>

      <Card title="Your voicemail drops">
        {isLoading ? (
          <LoadingPanel label="Loading voicemails…" />
        ) : drops.length === 0 ? (
          <EmptyPanel icon={Music} title="No voicemail drops yet" hint="Record or upload your first message above." />
        ) : (
          <ul className="space-y-2">
            {drops.map((vm) => (
              <li key={vm.id} className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-civix-panelLight border border-civix-border/60">
                <Music className="w-4 h-4 text-civix-purple shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{vm.name}</div>
                  <div className="text-[11px] text-white/40">{vm.durationSec ? `${vm.durationSec}s` : '—'}</div>
                </div>
                {vm.previewUrl && <audio controls preload="none" src={vm.previewUrl} className="h-8 max-w-[240px]" />}
                <button type="button" className="text-red-300 hover:text-red-200" onClick={() => remove(vm)} aria-label={`Delete ${vm.name}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
